import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Container,
  Header,
  Icon,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import { OmsProduct, OmsReport } from "../../lusca-search-api/generated-src";
import useOmsReport from "../../hooks/useOmsReport";
import { OmsFilterDraft } from "../../hooks/useOmsFilterCriteria";
import {
  clearOmsDraft,
  hasPendingOmsRequest,
  readOmsRecoveryDraft,
  writeOmsDraft,
} from "../../hooks/omsDraftStorage";
import OMSExportDialog from "./OMSExportDialog";
import OMSReportSchedule from "./OMSReportSchedule";
import OMSPersonalExportLink from "./OMSPersonalExportLink";
import { previousOmsExport } from "../../hooks/useOmsExport";
import "./omsReportDownload.scss";

function unavailableMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 404) return "No report is available yet.";
  if (status === 403) return "You do not have access to download this report.";
  return "The report is temporarily unavailable. Refresh to try again.";
}

export default function OMSReportDownload({
  product,
}: {
  product: OmsProduct;
}) {
  const query = useOmsReport(product);
  const [snapshot, setSnapshot] = useState<OmsReport | null>(null);
  const [resumeExport, setResumeExport] = useState(false);
  const [draft, setDraft] = useState<OmsFilterDraft | undefined>(() =>
    readOmsRecoveryDraft(product)
  );
  const saveDraft = useCallback(
    (next: OmsFilterDraft) => {
      writeOmsDraft(product, next);
      setDraft(next);
    },
    [product]
  );
  useEffect(() => {
    setSnapshot(null);
    setResumeExport(false);
    setDraft(readOmsRecoveryDraft(product));
  }, [product]);
  const productLabel = {
    [OmsProduct.Channels]: "PV Channels",
    [OmsProduct.Addon]: "PV Addon",
    [OmsProduct.Tvod]: "PV TVOD",
  }[product];

  return (
    <>
      <section
        className="oms-report-card"
        aria-label={`${productLabel} report`}
      >
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    iconName="refresh"
                    ariaLabel="Check for a newer report"
                    loading={query.isFetching}
                    loadingText="Refreshing report details"
                    wrapText={false}
                    onClick={() => {
                      query.refetch().catch(() => undefined);
                    }}
                  />
                  <Button
                    variant="primary"
                    iconName="download"
                    wrapText={false}
                    ariaHaspopup="dialog"
                    disabled={!query.data || query.isLoading || query.isError}
                    onClick={() => {
                      setDraft(readOmsRecoveryDraft(product));
                      setResumeExport(false);
                      setSnapshot(query.data || null);
                    }}
                  >
                    Export report
                  </Button>
                </SpaceBetween>
              }
            >
              <span className="oms-report-download__icon-label">
                <span aria-hidden="true">
                  <Icon name="file" variant="subtle" />
                </span>
                <span>{productLabel} report</span>
              </span>
            </Header>
          }
        >
          <SpaceBetween size="xs">
            {query.isLoading && (
              <div className="oms-report-download__status" role="status">
                <Spinner />
                <span>Loading report details…</span>
              </div>
            )}
            {query.isError && (
              <Alert
                type={
                  (query.error as { response?: { status?: number } })?.response
                    ?.status === 404
                    ? "info"
                    : "warning"
                }
                header="Report unavailable"
              >
                {unavailableMessage(query.error)}
              </Alert>
            )}
            {query.data && !query.isError && (
              <>
                <OMSReportSchedule
                  product={product}
                  sourceRefreshedAt={query.data.sourceRefreshedAt}
                />
                <OMSPersonalExportLink
                  report={query.data}
                  open={Boolean(snapshot)}
                  onOpen={() => {
                    const previous = previousOmsExport(product, query.data);
                    if (draft?.editing && previous)
                      saveDraft({
                        ...draft,
                        viewingPrevious: { mode: previous.mode },
                      });
                    setResumeExport(true);
                    setSnapshot(query.data || null);
                  }}
                />
              </>
            )}
          </SpaceBetween>
        </Container>
      </section>
      {snapshot && (
        <OMSExportDialog
          product={product}
          productLabel={productLabel}
          report={snapshot}
          availableReport={query.data || snapshot}
          draft={draft}
          resumeExport={resumeExport}
          onSaveDraft={saveDraft}
          onClose={(next) => {
            if (hasPendingOmsRequest(next)) saveDraft(next);
            else {
              clearOmsDraft(product);
              setDraft(undefined);
            }
            setSnapshot(null);
          }}
        />
      )}
    </>
  );
}
